import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Typography,
} from "@mui/material";
import {
  LEAD_TYPE_OPTIONS,
  LEAD_STATUS_OPTIONS,
  LEAD_PRIORITY_OPTIONS,
  LEAD_TEMPERATURE_OPTIONS,
} from "../types/crmTypes";

const LeadForm = ({ initialData, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    whatsapp: "",
    leadType: "wedding",
    inquiryType: "website_contact_form",
    source: {
      primary: "website",
      secondary: "",
      campaignId: "",
      campaignName: "",
    },
    requirements: {
      eventDate: "",
      eventLocation: "",
      guestCount: "",
      budget: "",
      servicesNeeded: [],
    },
    message: "",
    status: "new",
    pipelineStage: "new",
    priority: "medium",
    temperature: "warm",
    ownership: {
      assignedTo: "",
      teamId: "",
      branchId: "",
    },
    tags: [],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        fullName: initialData.fullName || "",
        firstName: initialData.firstName || "",
        lastName: initialData.lastName || "",
        email: initialData.email || "",
        phone: initialData.phone || "",
        whatsapp: initialData.whatsapp || "",
        leadType: initialData.leadType || "wedding",
        inquiryType: initialData.inquiryType || "website_contact_form",
        source: {
          primary: initialData.source?.primary || "website",
          secondary: initialData.source?.secondary || "",
          campaignId: initialData.source?.campaignId || "",
          campaignName: initialData.source?.campaignName || "",
        },
        requirements: {
          eventDate: initialData.requirements?.eventDate || "",
          eventLocation: initialData.requirements?.eventLocation || "",
          guestCount: initialData.requirements?.guestCount || "",
          budget: initialData.requirements?.budget || "",
          servicesNeeded: initialData.requirements?.servicesNeeded || [],
        },
        message: initialData.message || "",
        status: initialData.status || "new",
        pipelineStage: initialData.pipelineStage || "new",
        priority: initialData.priority || "medium",
        temperature: initialData.temperature || "warm",
        ownership: {
          assignedTo: initialData.ownership?.assignedTo || "",
          teamId: initialData.ownership?.teamId || "",
          branchId: initialData.ownership?.branchId || "",
        },
        tags: initialData.tags || [],
      });
    }
  }, [initialData]);

  const handleChange = (path, value) => {
    setFormData((prev) => {
      const updated = { ...prev };
      const keys = path.split(".");
      let obj = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }

      obj[keys[keys.length - 1]] = value;
      return updated;
    });

    if (errors[path]) setErrors((prev) => ({ ...prev, [path]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.fullName.trim() && !formData.firstName.trim()) {
      newErrors.fullName = "Lead name is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Divider>
          <Typography variant="caption">Lead Info</Typography>
        </Divider>

        <TextField
          label="Full Name *"
          value={formData.fullName}
          onChange={(e) => handleChange("fullName", e.target.value)}
          error={!!errors.fullName}
          helperText={errors.fullName}
          fullWidth
          disabled={loading}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
        </Grid>

        <Divider>
          <Typography variant="caption">Lead Details</Typography>
        </Divider>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Lead Type</InputLabel>
              <Select
                value={formData.leadType}
                onChange={(e) => handleChange("leadType", e.target.value)}
                label="Lead Type"
                disabled={loading}
              >
                {LEAD_TYPE_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Inquiry Type"
              value={formData.inquiryType}
              onChange={(e) => handleChange("inquiryType", e.target.value)}
              fullWidth
              disabled={loading}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Event Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.requirements.eventDate}
              onChange={(e) =>
                handleChange("requirements.eventDate", e.target.value)
              }
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Event Location"
              value={formData.requirements.eventLocation}
              onChange={(e) =>
                handleChange("requirements.eventLocation", e.target.value)
              }
              fullWidth
              disabled={loading}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Budget"
              type="number"
              value={formData.requirements.budget}
              onChange={(e) =>
                handleChange("requirements.budget", e.target.value)
              }
              fullWidth
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Guest Count"
              type="number"
              value={formData.requirements.guestCount}
              onChange={(e) =>
                handleChange("requirements.guestCount", e.target.value)
              }
              fullWidth
              disabled={loading}
            />
          </Grid>
        </Grid>

        <TextField
          label="Message"
          multiline
          rows={3}
          value={formData.message}
          onChange={(e) => handleChange("message", e.target.value)}
          fullWidth
          disabled={loading}
        />

        <Divider>
          <Typography variant="caption">Pipeline</Typography>
        </Divider>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleChange("status", e.target.value)}
                label="Status"
                disabled={loading}
              >
                {LEAD_STATUS_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => handleChange("priority", e.target.value)}
                label="Priority"
                disabled={loading}
              >
                {LEAD_PRIORITY_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Temperature</InputLabel>
              <Select
                value={formData.temperature}
                onChange={(e) => handleChange("temperature", e.target.value)}
                label="Temperature"
                disabled={loading}
              >
                {LEAD_TEMPERATURE_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
          >
            {loading
              ? "Saving..."
              : initialData
                ? "Update Lead"
                : "Create Lead"}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default LeadForm;
